import React, { useCallback, useContext, useMemo, useState } from "react";
import { Button, Navbar } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import Base from "./subcomponents/Base/Base";
import Sidebar from "./subcomponents/sidebar/Sidebar";
import { useLocation, useNavigate } from "react-router-dom";
import ThemePaletteControls from "../../components/preferences/ThemePaletteControls";
import { useI18n } from "../../i18n/I18nContext";
import { FiMenu } from "react-icons/fi";
import { GlobalContext } from "../../store/GlobalContext";
import "./admin.css";

const sectionBySlug: Record<string, string> = {
  home: 'Home',
  orders: 'Orders',
  carts: 'Carts',
  products: 'Products',
  'delivery-boys': 'DeliveryBoys',
  promotions: 'Promotions',
  announcements: 'Announcements',
  returns: 'Returns',
  logs: 'Logs',
  reviews: 'Reviews',
  categories: 'Categories',
  'contact-messages': 'ContactMessages',
  customers: 'Customers',
  'admin-users': 'AdminUsers',
  'registered-users': 'RegisteredUsers',
  settings: 'Settings',
};

const slugBySection: Record<string, string> = {
  Home: 'home',
  Orders: 'orders',
  Carts: 'carts',
  Products: 'products',
  DeliveryBoys: 'delivery-boys',
  Promotions: 'promotions',
  Announcements: 'announcements',
  Returns: 'returns',
  Logs: 'logs',
  Reviews: 'reviews',
  Categories: 'categories',
  ContactMessages: 'contact-messages',
  Customers: 'customers',
  AdminUsers: 'admin-users',
  RegisteredUsers: 'registered-users',
  Settings: 'settings',
};

const formatSectionTitle = (value: string) => {
  if (!value) return 'Dashboard';
  if (value === 'Home') return 'Dashboard';
  return value.replace(/([a-z])([A-Z])/g, '$1 $2').trim();
};

const AdminDashboard = () => {
  const [componentName, setComponentName] = useState("Home");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { state } = useContext(GlobalContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const userRoles: string[] = useMemo(
    () => (Array.isArray(state?.auth?.user?.roles) ? state.auth.user.roles : []),
    [state?.auth?.user?.roles],
  );

  const canAccessSection = useCallback((section: string) => {
    const superAdminOnlySections = ['AdminUsers', 'RegisteredUsers'];
    if (superAdminOnlySections.includes(section) && !userRoles.includes('super_admin')) {
      return false;
    }
    return true;
  }, [userRoles]);

  React.useEffect(() => {
    const slug = location.pathname.replace(/^\/admin\/?/, '').split('/')[0].toLowerCase();
    const nextSection = sectionBySlug[slug] || 'Home';

    if (!canAccessSection(nextSection)) {
      if (componentName !== 'Home') {
        setComponentName('Home');
      }
      if (location.pathname !== '/admin/home') {
        navigate('/admin/home', { replace: true });
      }
      return;
    }

    if (nextSection !== componentName) {
      setComponentName(nextSection);
    }
  }, [location.pathname, componentName, navigate, canAccessSection]);

  const onNavigateSection = (section: string) => {
    if (!canAccessSection(section)) {
      setSidebarOpen(false);
      navigate('/admin/home', { replace: true });
      return;
    }

    const slug = slugBySection[section] || 'home';
    setComponentName(section);
    navigate(`/admin/${slug}`);
    setSidebarOpen(false);
  };

  const sectionTitle = formatSectionTitle(componentName);

  return (
    <div className="admin-app">
      {sidebarOpen ? <button className="admin-overlay" onClick={() => setSidebarOpen(false)} aria-label="Close menu" /> : null}

      <div
        className={`admin-sidebar-shell ${sidebarOpen ? 'is-open' : ''}`}
      >
        <Sidebar setComponentName={onNavigateSection} componentName={componentName} />
      </div>

      <div className="admin-content-shell">
        <Navbar bg="light" expand="lg" className="mb-3 app-admin-navbar admin-topbar">
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-dark"
              size="sm"
              className="admin-menu-btn"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open navigation"
            >
              <FiMenu />
            </Button>
            <Navbar.Brand href="#" className="mb-0">{t('admin_panel')}</Navbar.Brand>
          </div>

          <div className="ms-auto d-flex align-items-center gap-2 app-top-actions">
            <ThemePaletteControls />
            <Button variant="outline-dark" size="sm" onClick={() => navigate('/')}>{t('admin_go_customer')}</Button>
          </div>
        </Navbar>

        <section className="admin-page-head">
          <h2>{sectionTitle}</h2>
          <p>Manage store operations with clearer workflows and faster actions.</p>
        </section>

        <div className="admin-content-area">
          <Base componentName={componentName} />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
